// @mui
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
// theme
import { bgBlur } from 'src/theme/css';
// hooks
import { useOffSetTop } from 'src/hooks/use-off-set-top';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import SvgColor from 'src/components/svg-color';
import { useSettingsContext } from 'src/components/settings';
import { useCurrentPageContext } from 'src/layouts/dashboard/contexts/useCurrentPageContext';
//
import { HEADER, NAV } from '../config-layout';
import { AccountPopover, SettingsButton } from '../_common';
// assets
import SiherLogo from 'src/assets/seal-siher.png';
import { Typography } from '@mui/material';

// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
  children?: React.ReactNode;
};

export default function Header({ onOpenNav, children }: Props) {
  const theme = useTheme();

  const settings = useSettingsContext();
  const { currentPage } = useCurrentPageContext();
  const isNavHorizontal = settings.themeLayout === 'horizontal';

  const isNavMini = settings.themeLayout === 'mini';

  const lgUp = useResponsive('up', 'lg');

  const offset = useOffSetTop(HEADER.H_DESKTOP);

  const offsetTop = offset && !isNavHorizontal;

  const renderLogo = (
    <img
      src={SiherLogo}
      alt="Siher Logo"
      style={{
        width: '40px',
      }}
    />
  );

  const renderContentVertical = (
    <>
      {!lgUp && (
        <IconButton onClick={onOpenNav}>
          <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
        </IconButton>
      )}
      <Stack>
        <Typography variant="h4" align="left">
          {currentPage.title}
        </Typography>
        <Typography variant="caption" align="left">
          {currentPage.subtitle}
        </Typography>
      </Stack>
      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1 }}
      >
        {children}
        <AccountPopover />
      </Stack>
    </>
  );

  const renderContentHorizontal = (
    <>
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        spacing={4}
      >
        {lgUp && renderLogo}
        {children}
      </Stack>

      {!lgUp && (
        <IconButton onClick={onOpenNav}>
          <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
        </IconButton>
      )}

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1 }}
      >
        <SettingsButton />

        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        borderBottom: `dashed 1px ${theme.palette.divider}`,
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(lgUp && {
          width: `calc(100% - ${NAV.W_VERTICAL + 1}px)`,
          height: HEADER.H_DESKTOP,
          ...(offsetTop && {
            height: HEADER.H_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {isNavHorizontal ? renderContentHorizontal : renderContentVertical}
      </Toolbar>
    </AppBar>
  );
}
